import {
  IdentifyInputSchema,
  IdentifyOutputSchema,
  IdentifyTransformSchema,
} from '../types/segment-events';
import type { IdentifyOutput } from '../types/segment-events';
import { safeValidate } from './frontend';

export function validateIdentifyInput(data: unknown) {
  return safeValidate(IdentifyInputSchema, data, 'identify_input');
}

export function validateIdentifyOutput(data: unknown) {
  return safeValidate(IdentifyOutputSchema, data, 'identify_output');
}

export function transformIdentifyEvent(
  data: unknown,
  formatters: {
    formatRole?: (value: string) => string | undefined;
    formatMemberStatus?: (value: string) => string | undefined;
    formatPlanCode?: (value: string) => string | undefined;
  } = {},
): { success: true; data: IdentifyOutput } | { success: false; error: string; field: string } {
  try {
    const inputValidation = IdentifyInputSchema.safeParse(data);
    if (!inputValidation.success) {
      return {
        success: false,
        error: 'Input validation failed',
        field: 'identify_input',
      };
    }

    const inputData = inputValidation.data;
    const transformedData = IdentifyTransformSchema.safeParse(inputData).data;

    const result: IdentifyOutput = {};

    const fieldMappings = [
      { key: 'role', value: transformedData?.role, formatter: formatters.formatRole },
      {
        key: 'member_status',
        value: transformedData?.member_status,
        formatter: formatters.formatMemberStatus,
      },
      {
        key: 'plan_code',
        value: transformedData?.plan_code,
        formatter: formatters.formatPlanCode,
        // Preserve an explicit empty plan_code so it overwrites (clears) a stale
        // trait in Segment for members with no plan code (Club/Refill-only).
        allowEmpty: true,
      },
      { key: 'experiment_name', value: transformedData?.experiment_name },
      { key: 'variation_name', value: transformedData?.variation_name },
      { key: 'feature_flag_name', value: transformedData?.feature_flag_name },
      { key: 'campaign_id', value: transformedData?.campaign_id },
      { key: 'fff_uuid', value: transformedData?.fff_uuid },
      { key: 'fff_session_id', value: transformedData?.fff_session_id },
    ];

    fieldMappings.forEach(({ key, value, formatter, allowEmpty }) => {
      const shouldInclude = allowEmpty ? value !== undefined && value !== null : Boolean(value);
      if (shouldInclude) {
        result[key] = formatter ? formatter(value) : value;
      }
    });

    return { success: true, data: result };
  } catch (_error) {
    return {
      success: false,
      error: 'Unexpected error during transformation',
      field: 'identify_transform',
    };
  }
}

export function validateIdentifyEvent(input: unknown, output: unknown) {
  return {
    input: validateIdentifyInput(input),
    output: validateIdentifyOutput(output),
  };
}
